import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Button from '../../../../ui/components/Button';
import MenuOpen from '../../../../ui/components/MenuOpen';
import Tooltip from '../../../../ui/components/Tooltip';
import ArtMenuMini from '../assets/ArtMenuMini';

const GameActionsMenuRoot = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    z-index: 100;
`;

// Define a type for menu states
type MenuState = null | 'actions' | 'art' | 'sound';

interface GameActionsMenuProps { 
    isInMentionMode?: boolean;
    mentionAnchorEl?: HTMLElement | null;
    onArtSelected?: (artId: string, aiImage: string, artName: string) => void;
    mentionFilter?: string;
    onActionSelected?: (actionType: string) => void;
    hideButtonMenu?: boolean;
}

const GameActionsMenu = ({ 
    isInMentionMode = false, 
    mentionAnchorEl = null, 
    onArtSelected, 
    mentionFilter = '',
    onActionSelected,
    hideButtonMenu = false
}: GameActionsMenuProps) => {
    const actionsButtonRef = useRef<HTMLDivElement>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [menuOpen, setMenuOpen] = useState<MenuState>(null);
    const [selectedArtIndex, setSelectedArtIndex] = useState<number | null>(null);

    const handleClick = () => {
        if (actionsButtonRef.current) {
            setAnchorEl(actionsButtonRef.current);
            setMenuOpen(menuOpen ? null : 'actions');
        }
    };

    const handleClose = () => {
        if (!isInMentionMode) {
            setMenuOpen(null);
            setAnchorEl(null);
        }
    };

    const handleOptionClick = (option_text: string) => {
        if (option_text === 'Art') {
            setMenuOpen('art');
            if (isInMentionMode && onActionSelected) {
                onActionSelected('Art');
            }
        } else if (option_text === 'Sound') {
            setMenuOpen('sound');
            if (isInMentionMode && onActionSelected) {
                onActionSelected('Sound');
            }
        }
    };

    // Determine which element to use as anchor based on mode
    const effectiveAnchorEl = isInMentionMode ? mentionAnchorEl : anchorEl;
    const effectiveActionsButtonRef = isInMentionMode ? { current: mentionAnchorEl } : actionsButtonRef;
    
    // Handle art selection based on mode
    const handleArtSelection = (artId: string, aiImage: string, artName: string) => {
        console.log(`Selected art: ${artName} (${artId})`);
        
        if (isInMentionMode && onArtSelected) {
            // In mention mode, call the provided callback
            onArtSelected(artId, aiImage, artName);
        } else {
            // In normal mode, just close the menu
            setMenuOpen(null);
        }
    };
    
    // Auto-open actions menu in mention mode
    useEffect(() => {
        if (isInMentionMode && mentionAnchorEl) {
            console.log('Mention mode active, opening actions menu');
            setMenuOpen('actions');
        }
    }, [isInMentionMode, mentionAnchorEl]);
    
    // Auto-select menu option based on mention type
    useEffect(() => {
        if (isInMentionMode && mentionFilter && menuOpen === 'actions') {
            // Check if the filter matches any of our options
            const options = ['art', 'sound'];
            const matchedOption = options.find(option => 
                mentionFilter.toLowerCase() === option.toLowerCase());
            
            if (matchedOption) {
                // Auto-select the matched option
                handleOptionClick(matchedOption.charAt(0).toUpperCase() + matchedOption.slice(1));
            }
        }
    }, [isInMentionMode, mentionFilter, menuOpen]);
    
    return (
        <>
            {!isInMentionMode && !hideButtonMenu && (
                <GameActionsMenuRoot ref={actionsButtonRef}>
                    <Tooltip text="Actions" colorMode="dark" hide={menuOpen === 'actions'}>
                        <Button 
                            size="tiny" 
                            color="ghost white" 
                            icon="at" 
                            onClick={handleClick}
                        />
                    </Tooltip>
                    <MenuOpen
                        anchorEl={anchorEl}
                        open={menuOpen === 'actions'}
                        onClose={handleClose}
                        options={[
                            { icon_left: "at", text: 'Sound', icon: 'sound', onClick: () => handleOptionClick('Sound') },
                            { icon_left: "at", text: 'Art', icon: 'image', onClick: () => handleOptionClick('Art') },
                        ]}
                        color="black"
                        minWidth={140}
                        placement="top-start"
                    />
                </GameActionsMenuRoot>
            )}

            {/* Actions Menu for mention mode */}
            {isInMentionMode && (() => {
                // Filter options based on mentionFilter
                const filteredOptions = [
                    { icon_left: "at", text: 'Sound', icon: 'sound', onClick: () => handleOptionClick('Sound') },
                    { icon_left: "at", text: 'Art', icon: 'image', onClick: () => handleOptionClick('Art') },
                ].filter(option => 
                    mentionFilter === '' || 
                    option.text.toLowerCase().includes(mentionFilter.toLowerCase())
                );
                
                // Only render the menu if there are options to display
                return filteredOptions.length > 0 ? (
                    <MenuOpen
                        anchorEl={mentionAnchorEl}
                        open={isInMentionMode && menuOpen === 'actions'}
                        onClose={handleClose}
                        options={filteredOptions}
                        color="black"
                        minWidth={140}
                        placement="bottom-start"
                    />
                ) : null;
            })()}

            {/* Art Menu for selecting art */}
            <ArtMenuMini 
                isOpen={menuOpen === 'art'} 
                onOpenChange={(open) => {
                    // console.log('on open change', open);
                    setMenuOpen(open ? 'art' : null);
                }}
                selectedArtIndex={selectedArtIndex}
                actionsButtonRef={effectiveActionsButtonRef as React.RefObject<HTMLDivElement>}
                onArtSelected={handleArtSelection}
                filterText={isInMentionMode ? mentionFilter : undefined}
            />
        </>
    );
};

export default GameActionsMenu;